import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { helpApi } from '../../lib/api'

export const TICKET_SUGGESTION_DEBOUNCE_MS = 300
export const TICKET_SUGGESTION_RECORD_WAIT_MS = 2000

export interface TicketSuggestionRecord {
  id: string
  token: string
}

export function waitForSuggestionRecord(
  pending: Promise<TicketSuggestionRecord | null> | null,
  timeoutMs: number,
): Promise<TicketSuggestionRecord | null> {
  if (!pending) return Promise.resolve(null)

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(null), timeoutMs)
    pending.then(
      (record) => {
        window.clearTimeout(timer)
        resolve(record)
      },
      () => {
        window.clearTimeout(timer)
        resolve(null)
      },
    )
  })
}

function useDebouncedPair(subject: string, body: string, delay: number): { subject: string; body: string } {
  const [debounced, setDebounced] = useState({ subject, body })

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced({ subject, body }), delay)
    return () => window.clearTimeout(timer)
  }, [subject, body, delay])

  return debounced
}

export function TicketArticleSuggestions({
  subject,
  body,
  onOutcomeChange,
  onPendingRecord,
  onOpened,
  onShown,
}: {
  subject: string
  body: string
  onOutcomeChange: (record: TicketSuggestionRecord) => void
  onPendingRecord: (pending: Promise<TicketSuggestionRecord | null>) => void
  onOpened: () => void
  onShown: () => void
}) {
  const debounced = useDebouncedPair(subject.trim(), body.trim(), TICKET_SUGGESTION_DEBOUNCE_MS)
  const enabled = `${debounced.subject} ${debounced.body}`.trim().length >= 3
  const outcomeIdRef = useRef<string | null>(null)
  const tokenRef = useRef<string | null>(null)
  const recordQueue = useRef(Promise.resolve())

  const search = useQuery({
    queryKey: ['ticket-article-suggestions', debounced.subject, debounced.body],
    queryFn: () => helpApi.searchTicketSuggestions({
      subject: debounced.subject,
      body: debounced.body,
    }),
    enabled,
    retry: false,
  })

  const articles = (search.isSuccess ? search.data?.data ?? [] : []).slice(0, 3)
  const shownKey = articles.map((article) => article.id).join(',')

  useEffect(() => {
    if (shownKey !== '') onShown()
  }, [shownKey, onShown])

  const recordOpen = (articleId: string) => {
    onOpened()
    const pending = recordQueue.current.then(async (): Promise<TicketSuggestionRecord | null> => {
      try {
        const currentId = outcomeIdRef.current
        const currentToken = tokenRef.current
        const row = currentId && currentToken
          ? await helpApi.updateTicketSuggestion(currentId, { article_id: articleId, token: currentToken })
          : await helpApi.createTicketSuggestion({ article_id: articleId })
        outcomeIdRef.current = row.id
        tokenRef.current = row.token
        onOutcomeChange({ id: row.id, token: row.token })
        return { id: row.id, token: row.token }
      } catch {
        return outcomeIdRef.current && tokenRef.current
          ? { id: outcomeIdRef.current, token: tokenRef.current }
          : null
      }
    }).catch(() => null)
    recordQueue.current = pending.then(() => undefined)
    onPendingRecord(pending)
  }

  return (
    <div aria-live="polite">
      {articles.length > 0 ? (
        <section aria-label="Suggested Help articles" className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <h3 className="text-sm font-medium text-amber-950">Suggested Help articles</h3>
          <ul className="mt-2 space-y-2">
            {articles.map((article) => (
              <li key={article.id}>
                <Link
                  to={`/help/${article.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-semibold text-blue-700 hover:text-blue-900"
                  onClick={() => recordOpen(article.id)}
                >
                  {article.title}
                </Link>
                {article.excerpt ? (
                  <p className="mt-0.5 text-xs text-amber-900">{article.excerpt}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}
